import dotsIcon from 'Assets/images/three-dots-vertical.svg';
import MoreActivityButton from 'Components/MoreActivityButtons';
import { MobXProviderContext, observer } from 'mobx-react';
import { IPromiseBasedObservable } from 'mobx-utils';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useConversationArchive } from 'Modules/conversation/hooks/useConversationArchive';
import { useTogglePinConversation } from 'Modules/conversation/store/pinned-conversations/index.requests';
import * as React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { Popover, PopoverContent, PopoverTrigger } from '~/lib/ui/popover';
import { useConversationListContext } from '../../../ConversationListContext';
import { usePopoverCloseEvents } from './hooks/usePopoverCloseEvents';
import { ConversationItemMoreActionsProps } from './index.types';

const ConversationItemMoreActions: React.FC<
  ConversationItemMoreActionsProps
> = ({ conversation, onPopoverContentHoverChange }) => {
  const {
    ui,
    participant: participantStore,
    person: personStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const { panelRef, isSecondaryMenuVisible } = useConversationListContext();

  const { mutate: togglePinConversation } = useTogglePinConversation();

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  /**
   * Manage popover state
   * Automatically closes on scroll, panel hide, or when trigger element leaves viewport
   */
  const { isOpen, open, close, preventAutoFocusRef } = usePopoverCloseEvents({
    panelRef,
    isSecondaryMenuVisible,
    triggerRef,
    onClose: () => {
      // Only clear global state if this conversation is the one currently selected
      if (ui.showMoreActionOptions.conversationId === conversation.id) {
        ui.setMoreActionOptions({ show: false, conversationId: '' });
      }
    },
  });

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        open();
        ui.setMoreActionOptions({
          conversationId: conversation.id,
          show: true,
        });
      } else {
        onPopoverContentHoverChange?.(false);
        close();
      }
    },
    [open, close, ui, conversation.id, onPopoverContentHoverChange]
  );

  const isConversationArchived = !!conversation.archived;

  const { openArchiveConversation } = useConversationArchive({
    conversationId: conversation.id,
    isArchived: isConversationArchived,
    doWhenOpen: close,
    doWhenFinished: close,
  });

  const toggleMuteStatus = React.useCallback(
    (conversationid: string): boolean => {
      const value = ui.selectIfConvMuted(conversationid);
      ui.setConvMuted(conversationid, !value);
      close();
      return !value;
    },
    [ui, close]
  );

  const handleSetIsConversationPinned = React.useCallback(
    (conversationId: string) => {
      togglePinConversation(conversationId);
      close();
    },
    [togglePinConversation, close]
  );

  const handleButtonClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
    },
    []
  );

  let personPbo: IPromiseBasedObservable<ContactModelV2> | null = null;
  // If one on one conversation, get the other participant contact
  if (conversation.grouping === 'OneOnOne') {
    const otherParticipants =
      participantStore.selectOtherParticipants(conversation.id) ?? [];

    const otherPersonId = otherParticipants[0]?.personId;

    if (otherParticipants.length === 1 && otherPersonId) {
      personPbo = personStore.loadPersonByIdGetIfMissingGet(
        otherPersonId.toString()
      );
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          onClick={handleButtonClick}
          className="flex items-center justify-center w-6 h-6 hover:bg-accent rounded cursor-pointer shrink-0"
          data-testid="conversation-more-actions-trigger"
          id={`conversation-${conversation.id}-more-actions`}
        >
          <img className="w-4 h-4" src={dotsIcon} alt="More options" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="center"
        className="p-0"
        onEscapeKeyDown={close}
        onMouseEnter={() => onPopoverContentHoverChange?.(true)}
        onMouseLeave={() => onPopoverContentHoverChange?.(false)}
        onCloseAutoFocus={(e) => {
          if (preventAutoFocusRef.current) {
            e.preventDefault();
          }
        }}
      >
        <MoreActivityButton
          currentPersonId={undefined}
          toggleMuteStatus={toggleMuteStatus}
          showMoreActionOptions={isOpen}
          conversation={conversation}
          toggleConfirmationModal={openArchiveConversation}
          from={isConversationArchived ? 'archived' : 'recentFevList'}
          setIsConversationPinned={handleSetIsConversationPinned}
          personPbo={personPbo}
          onClose={close}
        />
      </PopoverContent>
    </Popover>
  );
};

export default observer(ConversationItemMoreActions);
